import { onMounted } from 'vue';
import { useAssetStore } from '@/stores/asset';
import axios from '@/plugins/axiosInstance';
import { settings } from '@/networking/urlManager';
import { responseHandlerService } from '@/plugins/responseHandlerService';

// State
import { createState } from './state/index';

// Utils
import {
  getFileTypeName,
  getTypesAll,
  getStateText,
  getStateColor,
  getStateIcon,
  formatFileSize
} from '../../utils';

// Methods
import { 
  createDocumentDeleteMethods,
  createDocumentLoadMethods,
  createDocumentCategoryMethods,
  createDocumentUploadMethods,
  createDocumentFilterMethods,
  createDocumentPaginationMethods,
  createDocumentUIMethods
} from './methods';

/**
 * Asset bilgilerinde belge yönetimi için composable
 * 
 * @returns {Object} - Asset bilgileri durumu ve yöntemleri
 */
export const useAssetsInformation = () => {
  // Services
  const assetStore = useAssetStore();
  const services = {
    assetStore,
    axios,
    settings,
    responseHandlerService
  };
  
  // State
  const state = createState();
  
  // Method factories - Önce yükleme işlemlerini tanımla
  const loadMethods = createDocumentLoadMethods(state, services);
  
  // Diğer metotları oluştur
  const deleteMethods = createDocumentDeleteMethods(state, services, loadMethods);
  const categoryMethods = createDocumentCategoryMethods(state, services);
  const uploadMethods = createDocumentUploadMethods(state, services);
  const filterMethods = createDocumentFilterMethods(state, services);
  const paginationMethods = createDocumentPaginationMethods(state);
  const uiMethods = createDocumentUIMethods(state);
  
  // Cross dependencies
  const enhancedFilterMethods = {
    ...filterMethods,
    applyFilters: () => filterMethods.applyFilters(loadMethods.filterDocuments),
    resetFilters: () => {
      filterMethods.resetFilters();
      filterMethods.applyFilters(loadMethods.filterDocuments);
    },
    removeFilter: (filterKey) => {
      filterMethods.removeFilter(filterKey);
      filterMethods.applyFilters(loadMethods.filterDocuments);
    }
  };
  
  const enhancedDeleteMethods = {
    ...deleteMethods,
    confirmDeleteDocument: async (ids) => {
      try {
        // Silme işlemi başlamadan önce hata mesajını temizle
        if (state.deleteErrorMessage) {
          state.deleteErrorMessage.value = '';
        }
        state.isDeleting.value = true;
        
        // Belge ID'lerini al
        const documentIds = ids || (state.documentToDelete.value ? [state.documentToDelete.value] : []);
        
        if (!documentIds || documentIds.length === 0) {
          if (state.deleteErrorMessage) {
            state.deleteErrorMessage.value = 'Silmek için belge seçilmedi';
          }
          return;
        }
        
        // Silme işlemini çağır
        await deleteMethods.handleBulkDeleteDocuments(documentIds);
        
        // Başarılı silme işlemi - temizlik işlemleri
        state.documentToDelete.value = null;
        state.showDeleteConfirm.value = false;
        
        // Belgeleri yeniden yükle
        await loadMethods.loadDocuments();
      } catch (error) {
        if (state.deleteErrorMessage) {
          state.deleteErrorMessage.value = ids && ids.length > 1 
            ? 'Belgeler silinirken bir hata oluştu. Lütfen tekrar deneyin.'
            : 'Belge silinirken bir hata oluştu. Lütfen tekrar deneyin.';
        }
      } finally {
        state.isDeleting.value = false;
      }
    }
  };
  
  const enhancedCategoryMethods = {
    ...categoryMethods,
    handleUpdateCategories: async (documentId, categoryIds, documentAssetId) => {
      await categoryMethods.handleUpdateCategories(documentId, categoryIds, documentAssetId);
      loadMethods.loadDocuments(); // Categories değiştikten sonra belgeleri yenile
    }
  };
  
  // Get upload methods in full
  const enhancedUploadMethods = {
    ...uploadMethods,
    handleUploadFiles: async () => {
      await uploadMethods.handleUploadFiles();
      loadMethods.loadDocuments(); // Yükleme işlemi tamamlandıktan sonra belgeleri yenile
    }
  };
  
  // Tüm metodları birleştir
  // Sıralama metodları
  const sortMethods = {
    // Sıralama durumunu güncelle ve belgeleri yeniden sırala
    handleSortChange: (sortOptions) => {
      if (sortOptions && sortOptions.field) {
        // Sıralama durumunu güncelle
        state.sortState.value = {
          field: sortOptions.field,
          direction: sortOptions.direction || 'desc'
        };
        
        // Sıralama tarihçesini de güncelle - VTable'ın sıralama oklarını düzgün göstermek için
        if (state.sortHistory && state.sortHistory.value) {
          state.sortHistory.value = {
            lastColumn: sortOptions.field,
            columnStates: {
              ...state.sortHistory.value.columnStates,
              [sortOptions.field]: sortOptions.direction.toUpperCase()
            }
          };
        }
        
        // Belgeleri yeniden filtrele ve sırala
        loadMethods.filterDocuments();
      }
    }
  };
  
  const methods = {
    ...loadMethods,
    ...enhancedDeleteMethods,
    ...enhancedCategoryMethods,
    ...enhancedUploadMethods,
    ...enhancedFilterMethods,
    ...paginationMethods,
    ...uiMethods,
    ...sortMethods
  };
  
  // Lifecycle
  onMounted(() => {
    methods.loadCategories();
    methods.loadDocuments();
  });
  
  // Public API
  return {
    // State
    ...state,
    
    // Methods
    ...methods,
    
    // Utils
    getFileTypeName,
    getTypesAll,
    getStateText,
    getStateColor,
    getStateIcon,
    formatFileSize
  };
};